const utcInstant = (v) => {
    const t = Date.parse(v);
    if (!Number.isFinite(t))
        throw new RangeError('a TV window is two UTC instants');
    return new Date(t).toISOString();
};
const DATES = `($2::timestamptz AT TIME ZONE 'UTC')::date - 1 AND ($3::timestamptz AT TIME ZONE 'UTC')::date + 1`;
/** The orders of the window: `f.market_id = $1`, the two verticals, requested inside `[$2, $3)`. */
const ORDERS = `f.market_id = $1 AND f.vertical IN ('RIDE','DELIVERY') AND f.date_key BETWEEN ${DATES}
   AND f.requested_at >= $2::timestamptz AND f.requested_at < $3::timestamptz`;
const bind = (marketId, w) => [marketId, utcInstant(w.from), utcInstant(w.to)];
const bindSet = (marketIds, w) => [[...marketIds], utcInstant(w.from), utcInstant(w.to)];
/** The same predicate as {@link ORDERS} over a SET of markets (`$1` is a text array): the percentile and the peak are not sums of their markets'. */
const ORDERS_OF_SET = ORDERS.replace('f.market_id = $1', 'f.market_id = ANY($1::text[])');
/** Rides and deliveries by service, with the sums the means are made of (a mean over zero samples is not a number: the caller keeps `n`). */
export function tvOrdersQuery(marketId, w) {
    return {
        sql: `SELECT f.vertical::text AS vertical, COALESCE(f.category_code, '')::text AS category_code,
       count(*)::int AS requested,
       (count(*) FILTER (WHERE f.status = 'COMPLETED'))::int AS completed,
       (count(*) FILTER (WHERE f.status = 'CANCELLED'))::int AS cancelled,
       (count(*) FILTER (WHERE f.outcome = 'IN_PROGRESS'))::int AS in_progress,
       (count(f.actual_pickup_seconds))::int AS pickup_n, COALESCE(sum(f.actual_pickup_seconds), 0)::bigint::text AS pickup_sum,
       (count(f.accept_seconds))::int AS match_n, COALESCE(sum(f.accept_seconds), 0)::bigint::text AS match_sum,
       (count(f.transit_seconds) FILTER (WHERE f.status = 'COMPLETED'))::int AS trip_n,
       COALESCE(sum(f.transit_seconds) FILTER (WHERE f.status = 'COMPLETED'), 0)::bigint::text AS trip_sum
  FROM analytics.fact_order f WHERE ${ORDERS} GROUP BY 1, 2`,
        params: bind(marketId, w),
    };
}
/** The rides and deliveries still in progress (the "active now" of the services not read live), requested in the last hours. */
export function tvActiveQuery(marketId, w) {
    return {
        sql: `SELECT f.vertical::text AS vertical, COALESCE(f.category_code, '')::text AS category_code, count(*)::int AS active
  FROM analytics.fact_order f WHERE ${ORDERS} AND f.outcome = 'IN_PROGRESS' GROUP BY 1, 2`,
        params: bind(marketId, w),
    };
}
export function tvHoursQuery(marketId, w) {
    return {
        sql: `SELECT f.date_key::text AS day, f.hour_key::int AS hour, f.hour_occurrence::int AS occurrence, count(*)::int AS requested,
       (count(*) FILTER (WHERE f.status = 'COMPLETED'))::int AS completed,
       (count(*) FILTER (WHERE f.outcome = 'NO_DRIVER'))::int AS no_driver,
       (count(*) FILTER (WHERE f.status = 'CANCELLED'))::int AS cancelled
  FROM analytics.fact_order f WHERE ${ORDERS} GROUP BY 1, 2, 3 ORDER BY 1, 2, 3`,
        params: bind(marketId, w),
    };
}
export function tvMoneyQuery(marketId, w) {
    return {
        sql: `SELECT m.currency::text AS currency, m.vertical::text AS vertical, COALESCE(o.category_code, '')::text AS category_code,
       count(*)::int AS postings, COALESCE(sum(m.gross_minor), 0)::text AS gross
  FROM analytics.fact_money m
  LEFT JOIN analytics.fact_order o ON o.market_id = m.market_id AND o.vertical = m.vertical AND o.order_id = m.order_id
                                  AND o.date_key BETWEEN m.date_key - 3 AND m.date_key + 1
 WHERE m.market_id = $1
   AND ((m.vertical = 'RIDE' AND m.source_kind IN ('RIDE_CASH', 'RIDE_SETTLEMENT')) OR (m.vertical = 'DELIVERY' AND m.source_kind = 'ORDER_BOOKING'))
   AND m.date_key BETWEEN ${DATES}
   AND m.posted_at >= $2::timestamptz AND m.posted_at < $3::timestamptz
 GROUP BY 1, 2, 3`,
        params: bind(marketId, w),
    };
}
export function tvOffersQuery(marketId, w) {
    return {
        sql: `SELECT COALESCE(o.category_code, '')::text AS category_code,
       (count(*) FILTER (WHERE f.status = 'ACCEPTED'))::int AS accepted,
       (count(*) FILTER (WHERE f.status IN ('ACCEPTED', 'REJECTED', 'EXPIRED')))::int AS decided
  FROM analytics.fact_offer f
  LEFT JOIN analytics.fact_order o ON o.market_id = f.market_id AND o.vertical = 'RIDE' AND o.order_id = f.ride_id AND o.date_key BETWEEN f.date_key - 1 AND f.date_key + 1
 WHERE f.market_id = $1 AND f.date_key BETWEEN ${DATES}
   AND f.created_at >= $2::timestamptz AND f.created_at < $3::timestamptz
 GROUP BY 1`,
        params: bind(marketId, w),
    };
}
export function tvMatchQuery(marketIds, w) {
    return {
        sql: `SELECT (percentile_cont(0.5) WITHIN GROUP (ORDER BY f.accept_seconds))::float8 AS p50,
       (percentile_cont(0.95) WITHIN GROUP (ORDER BY f.accept_seconds))::float8 AS p95, count(*)::int AS n
  FROM analytics.fact_order f WHERE ${ORDERS_OF_SET} AND f.accept_seconds IS NOT NULL`,
        params: bindSet(marketIds, w),
    };
}
export function tvPeakQuery(marketIds, w) {
    return {
        sql: `WITH waited AS (
  SELECT f.requested_at AS t0, COALESCE(f.accepted_at, f.cancelled_at) AS t1
    FROM analytics.fact_order f WHERE ${ORDERS_OF_SET} AND COALESCE(f.accepted_at, f.cancelled_at) IS NOT NULL AND COALESCE(f.accepted_at, f.cancelled_at) >= f.requested_at
), moves AS (
  SELECT t0 AS t, 1 AS d FROM waited UNION ALL SELECT t1 AS t, -1 AS d FROM waited
), running AS (
  SELECT t, sum(d) OVER (ORDER BY t, d ROWS UNBOUNDED PRECEDING)::int AS n FROM moves
)
SELECT n AS peak, to_char(t AT TIME ZONE 'UTC', 'YYYY-MM-DD"T"HH24:MI:SS.MS"Z"') AS at FROM running ORDER BY n DESC, t LIMIT 1`,
        params: bindSet(marketIds, w),
    };
}
//# sourceMappingURL=tv.js.map